import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BsGrid, BsChevronLeft, BsChevronRight, BsChevronDown, BsBoxArrowRight, BsGear, BsPeople, BsEnvelope, BsPerson, BsBullseye, BsCalendar, BsListCheck, BsBriefcase, BsKanban, BsWhatsapp, BsMegaphone, BsImages, BsCollection, BsGraphUp, BsFileText, BsReceipt, BsArrowRepeat, BsClock, BsCpu, BsHeadset, BsBell, BsBellFill, BsActivity, BsRobot, BsTools, BsBuilding, BsCalculator } from 'react-icons/bs';
import './Sidebar.css';
import { apiFetch } from '../utils/api';
import usePushSubscription from '../hooks/usePushSubscription';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState('');
  const isCaptionActive = location.pathname.startsWith('/captacion')
    || location.pathname.startsWith('/captacion-business')
    || location.pathname.startsWith('/captacion-emails')
    || location.pathname.startsWith('/captacion-borme')
    || location.pathname.startsWith('/google-ads')
    || location.pathname.startsWith('/conexiones-google')
    || location.pathname.startsWith('/presupuesto-1-minuto');
  const isSalesActive = location.pathname.startsWith('/servicios')
    || location.pathname.startsWith('/facturas')
    || location.pathname.startsWith('/presupuestos')
    || location.pathname.startsWith('/dominios')
    || location.pathname.startsWith('/hosting');
  const isCommsActive = location.pathname === '/clientes/notificaciones'
    || location.pathname.startsWith('/comunicaciones')
    || location.pathname.startsWith('/email-marketing')
    || location.pathname.startsWith('/whatsapp');
  const [captionOpen, setCaptionOpen] = useState(isCaptionActive);
  const [salesOpen, setSalesOpen] = useState(isSalesActive);
  const [commsOpen, setCommsOpen] = useState(isCommsActive);
  const modules = React.useMemo(() => {
    try {
      const raw = localStorage.getItem('modules');
      return raw ? JSON.parse(raw) : null;
    } catch (_e) {
      return null;
    }
  }, []);

  React.useEffect(() => {
    const handler = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Logo de empresa (el mismo del área de cliente) vía endpoint público de marca
  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await apiFetch('/api/branding');
        if (!res.ok) return;
        const data = await res.json();
        if (active && data && data.company_logo) setCompanyLogo(data.company_logo);
      } catch (_e) { /* usa el logo por defecto */ }
    })();
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    if (isCaptionActive) {
      setCaptionOpen(true);
      setSalesOpen(false);
      setCommsOpen(false);
    }
  }, [isCaptionActive]);

  React.useEffect(() => {
    if (isSalesActive) {
      setSalesOpen(true);
      setCaptionOpen(false);
      setCommsOpen(false);
    }
  }, [isSalesActive]);

  React.useEffect(() => {
    if (isCommsActive) {
      setCommsOpen(true);
      setCaptionOpen(false);
      setSalesOpen(false);
    }
  }, [isCommsActive]);

  const canAccess = (slug) => {
    if (!modules || !Array.isArray(modules) || modules.length === 0) return true;
    return modules.includes(slug);
  };

  const canAccessCaption = canAccess('captacion')
    || canAccess('captacion_business')
    || canAccess('captacion_borme')
    || canAccess('google_ads')
    || canAccess('google_api');
  const canAccessSales = canAccess('services')
    || canAccess('facturas')
    || canAccess('presupuestos')
    || canAccess('dominios')
    || canAccess('hosting');
  const canAccessSupport = canAccess('soporte');
  const canAccessComms = canAccess('clientes')
    || canAccess('comunicaciones')
    || canAccess('email_marketing')
    || canAccess('whatsapp');

  // Notificaciones push del staff (globales, no atadas al módulo WhatsApp).
  // Se ofrece a quien pueda recibirlas: WhatsApp o Soporte (los admin ven todo).
  const { permission: pushPermission, ready: pushReady, subscribing: pushSubscribing, sync: syncPush, supported: pushSupported } = usePushSubscription();
  const canUsePush = canAccess('whatsapp') || canAccessSupport;
  const enablePush = async () => {
    if (pushPermission === 'denied') {
      window.alert('Has bloqueado las notificaciones en el navegador. Actívalas desde los ajustes del sitio (icono del candado en la barra de direcciones).');
      return;
    }
    await syncPush({ requestPermission: true });
  };

  // Tickets de soporte sin leer (badge): sondea cada 45s si el usuario tiene acceso.
  const [supportUnread, setSupportUnread] = useState(0);
  React.useEffect(() => {
    if (!canAccessSupport) return undefined;
    let active = true;
    const token = localStorage.getItem('token');
    const load = async () => {
      try {
        const res = await apiFetch('/api/support/unread-count', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (active) setSupportUnread(Number(data.count) || 0);
      } catch (_e) { /* silencioso */ }
    };
    load();
    const iv = setInterval(load, 45000);
    return () => { active = false; clearInterval(iv); };
  }, [canAccessSupport]);

  // Cambios solicitados por clientes que esperan al equipo (badge en "Cambios"): sondea cada 60s.
  const canAccessProjects = canAccess('proyectos');
  const [devAttention, setDevAttention] = useState(0);
  React.useEffect(() => {
    if (!canAccessProjects) return undefined;
    let active = true;
    const token = localStorage.getItem('token');
    const load = async () => {
      try {
        const res = await apiFetch('/api/dev-requests/counts', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (active) setDevAttention(Number(data.attention) || 0);
      } catch (_e) { /* silencioso */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { active = false; clearInterval(iv); };
  }, [canAccessProjects]);

  // Correo entrante sin leer (badge en "Correo"): sondea cada 60s si el usuario tiene acceso.
  const canAccessMail = canAccess('comunicaciones');
  const [commsUnread, setCommsUnread] = useState(0);
  React.useEffect(() => {
    if (!canAccessMail) return undefined;
    let active = true;
    const token = localStorage.getItem('token');
    const load = async () => {
      try {
        const res = await apiFetch('/api/communications/unread-count', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (active) setCommsUnread(Number(data.count) || 0);
      } catch (_e) { /* silencioso */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { active = false; clearInterval(iv); };
  }, [canAccessMail]);

  const isActive = (path) => {
    if (path === '/captacion-business') return location.pathname.startsWith('/captacion-business');
    if (path === '/captacion-emails') return location.pathname.startsWith('/captacion-emails');
    if (path === '/captacion-borme') return location.pathname.startsWith('/captacion-borme');
    if (path === '/google-ads') return location.pathname.startsWith('/google-ads');
    if (path === '/presupuesto-1-minuto') return location.pathname.startsWith('/presupuesto-1-minuto');
    if (path === '/clientes') return location.pathname.startsWith('/clientes');
    if (path === '/comunicaciones') return location.pathname.startsWith('/comunicaciones');
    if (path === '/proyectos') return location.pathname.startsWith('/proyectos');
    if (path === '/cambios') return location.pathname.startsWith('/cambios') || location.pathname.startsWith('/ajustes');
    if (path === '/jornada') return location.pathname.startsWith('/jornada');
    if (path === '/facturas-recurrentes') return location.pathname.startsWith('/facturas-recurrentes');
    if (path === '/facturas') return location.pathname.startsWith('/facturas') && !location.pathname.startsWith('/facturas-recurrentes');
    if (path === '/presupuestos') return location.pathname.startsWith('/presupuestos');
    if (path === '/dominios') return location.pathname.startsWith('/dominios');
    if (path === '/hosting') return location.pathname.startsWith('/hosting');
    if (path === '/captacion') {
      return location.pathname.startsWith('/captacion')
        && !location.pathname.startsWith('/captacion-business')
        && !location.pathname.startsWith('/captacion-emails')
        && !location.pathname.startsWith('/captacion-borme');
    }
    if (path === '/captacion-business') return location.pathname.startsWith('/captacion-business');
    if (path === '/whatsapp') return location.pathname.startsWith('/whatsapp');
    if (path === '/soporte') return location.pathname.startsWith('/soporte');
    if (path === '/email-marketing') return location.pathname.startsWith('/email-marketing');
    if (path === '/servicios') return location.pathname.startsWith('/servicios');
    if (path === '/agentes') return location.pathname.startsWith('/agentes');
    return location.pathname === path;
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen((o) => !o);
    } else {
      setCollapsed((c) => !c);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('token');
    try {
      if (token && typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          const subscription = await registration.pushManager.getSubscription();
          if (subscription?.endpoint) {
            await apiFetch('/api/push/unsubscribe', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ endpoint: subscription.endpoint })
            });
            await subscription.unsubscribe().catch(() => {});
          }
        }
      }
    } catch (_err) {
      // best-effort
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('modules');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('waPushVapidKey');
      navigate('/login');
    }
  };

  const handleNavigate = () => {
    if (isMobile) setMobileOpen(false);
  };

  return (
    <>
      <button className="sidebar-burger" onClick={() => setMobileOpen(true)} aria-label="Abrir menú">
        ☰
      </button>
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}
      <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'open' : ''}`}>
        <div className="sidebar-header p-3">
          <button className="toggle-btn" onClick={toggleSidebar} aria-label="Contraer menú">
            {collapsed ? <BsChevronRight /> : <BsChevronLeft />}
          </button>
          {!collapsed && companyLogo && <img src={companyLogo} alt="Logo" className="sidebar-logo" />}
        </div>

      <ul className="nav flex-column px-2">
        <li className={`nav-item ${isActive('/') ? 'active' : ''}`}>
          <Link className="nav-link d-flex align-items-center" to="/" onClick={handleNavigate}>
            <BsGrid className="me-2" /> {!collapsed && 'Dashboard'}
          </Link>
        </li>
        {canAccess('clientes') && (
          <li className={`nav-item ${isActive('/clientes') ? 'active' : ''}`}>
            <Link className="nav-link d-flex align-items-center" to="/clientes" onClick={handleNavigate}>
              <BsPeople className="me-2" /> {!collapsed && 'Clientes'}
            </Link>
          </li>
        )}
        {canAccessComms && (
          <li className={`nav-item ${isCommsActive ? 'active' : ''}`}>
            <button
              type="button"
              className="nav-link caption-toggle d-flex align-items-center w-100 border-0 bg-transparent"
              onClick={() => {
                setCommsOpen((open) => !open);
                setCaptionOpen(false);
                setSalesOpen(false);
              }}
            >
              <BsEnvelope className="me-2" /> {!collapsed && 'Comunicaciones'}
              {!collapsed && (commsOpen ? <BsChevronDown className="ms-auto" /> : <BsChevronRight className="ms-auto" />)}
            </button>
            {commsOpen && (
              <ul className="nav flex-column sidebar-submenu">
                {canAccess('clientes') && (
                  <li className={`nav-item ${location.pathname === '/clientes/notificaciones' ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/clientes/notificaciones" onClick={handleNavigate}>
                      <BsBell className="me-2" /> {!collapsed && 'Notificaciones'}
                    </Link>
                  </li>
                )}
                {canAccess('comunicaciones') && (
                  <li className={`nav-item ${isActive('/comunicaciones') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/comunicaciones" onClick={handleNavigate}>
                      <BsEnvelope className="me-2" /> {!collapsed && 'Correo'}
                      {commsUnread > 0 && (!collapsed
                        ? <span className="badge bg-primary ms-auto">{commsUnread}</span>
                        : <span title={`${commsUnread} sin leer`} style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: '50%', background: '#0d6efd', display: 'inline-block' }} />
                      )}
                    </Link>
                  </li>
                )}
                {canAccess('email_marketing') && (
                  <li className={`nav-item ${isActive('/email-marketing') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/email-marketing" onClick={handleNavigate}>
                      <BsMegaphone className="me-2" /> {!collapsed && 'Email marketing'}
                    </Link>
                  </li>
                )}
                {canAccess('whatsapp') && (
                  <li className={`nav-item ${isActive('/whatsapp') && !isActive('/whatsapp/qr') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/whatsapp" onClick={handleNavigate}>
                      <BsWhatsapp className="me-2" /> {!collapsed && 'Whatsapp'}
                    </Link>
                  </li>
                )}
                {canAccess('whatsapp') && (
                  <li className={`nav-item ${isActive('/whatsapp/qr') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/whatsapp/qr" onClick={handleNavigate}>
                      <BsWhatsapp className="me-2" /> {!collapsed && 'WhatsApp por QR'}
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </li>
        )}
        {canAccessCaption && (
          <li className={`nav-item ${isCaptionActive ? 'active' : ''}`}>
            <button
              type="button"
              className="nav-link caption-toggle d-flex align-items-center w-100 border-0 bg-transparent"
              onClick={() => {
                setCaptionOpen((open) => !open);
                setSalesOpen(false);
              }}
            >
              <BsBullseye className="me-2" /> {!collapsed && 'Caption'}
              {!collapsed && (captionOpen ? <BsChevronDown className="ms-auto" /> : <BsChevronRight className="ms-auto" />)}
            </button>
            {captionOpen && (
              <ul className="nav flex-column sidebar-submenu">
                {canAccess('captacion') && (
                  <li className={`nav-item ${isActive('/captacion') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/captacion" onClick={handleNavigate}>
                      <BsBullseye className="me-2" /> {!collapsed && 'Captación'}
                    </Link>
                  </li>
                )}
                {canAccess('captacion_business') && (
                  <li className={`nav-item ${isActive('/captacion-business') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/captacion-business" onClick={handleNavigate}>
                      <BsBriefcase className="me-2" /> {!collapsed && 'Captación Business'}
                    </Link>
                  </li>
                )}
                {canAccess('captacion_business') && (
                  <li className={`nav-item ${isActive('/captacion-emails') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/captacion-emails" onClick={handleNavigate}>
                      <BsEnvelope className="me-2" /> {!collapsed && 'Obtener emails'}
                    </Link>
                  </li>
                )}
                {canAccess('captacion_borme') && (
                  <li className={`nav-item ${isActive('/captacion-borme') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/captacion-borme" onClick={handleNavigate}>
                      <BsBuilding className="me-2" /> {!collapsed && 'Captación Borme'}
                    </Link>
                  </li>
                )}
                {canAccess('google_api') && (
                  <li className={`nav-item ${isActive('/conexiones-google') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/conexiones-google" onClick={handleNavigate}>
                      <BsGraphUp className="me-2" /> {!collapsed && 'Conexiones Google'}
                    </Link>
                  </li>
                )}
                {canAccess('google_ads') && (
                  <li className={`nav-item ${isActive('/google-ads') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/google-ads" onClick={handleNavigate}>
                      <BsGraphUp className="me-2" /> {!collapsed && 'Webhook'}
                    </Link>
                  </li>
                )}
                {canAccess('google_ads') && (
                  <li className={`nav-item ${isActive('/presupuesto-1-minuto') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/presupuesto-1-minuto" onClick={handleNavigate}>
                      <BsCalculator className="me-2" /> {!collapsed && 'Presupuesto en 1 minuto'}
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </li>
        )}
        {canAccessSales && (
          <li className={`nav-item ${isSalesActive ? 'active' : ''}`}>
            <button
              type="button"
              className="nav-link caption-toggle d-flex align-items-center w-100 border-0 bg-transparent"
              onClick={() => {
                setSalesOpen((open) => !open);
                setCaptionOpen(false);
              }}
            >
              <BsReceipt className="me-2" /> {!collapsed && 'Ventas'}
              {!collapsed && (salesOpen ? <BsChevronDown className="ms-auto" /> : <BsChevronRight className="ms-auto" />)}
            </button>
            {salesOpen && (
              <ul className="nav flex-column sidebar-submenu">
                {canAccess('services') && (
                  <li className={`nav-item ${isActive('/servicios') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/servicios" onClick={handleNavigate}>
                      <BsCollection className="me-2" /> {!collapsed && 'Servicios'}
                    </Link>
                  </li>
                )}
                {canAccess('facturas') && (
                  <li className={`nav-item ${isActive('/facturas') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/facturas" onClick={handleNavigate}>
                      <BsFileText className="me-2" /> {!collapsed && 'Facturas'}
                    </Link>
                  </li>
                )}
                {canAccess('facturas') && (
                  <li className={`nav-item ${isActive('/facturas-recurrentes') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/facturas-recurrentes" onClick={handleNavigate}>
                      <BsArrowRepeat className="me-2" /> {!collapsed && 'Facturas recurrentes'}
                    </Link>
                  </li>
                )}
                {canAccess('presupuestos') && (
                  <li className={`nav-item ${isActive('/presupuestos') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/presupuestos" onClick={handleNavigate}>
                      <BsFileText className="me-2" /> {!collapsed && 'Presupuestos'}
                    </Link>
                  </li>
                )}
                {canAccess('dominios') && (
                  <li className={`nav-item ${isActive('/dominios') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/dominios" onClick={handleNavigate}>
                      <BsCollection className="me-2" /> {!collapsed && 'Dominios'}
                    </Link>
                  </li>
                )}
                {canAccess('hosting') && (
                  <li className={`nav-item ${isActive('/hosting') ? 'active' : ''}`}>
                    <Link className="nav-link d-flex align-items-center" to="/hosting" onClick={handleNavigate}>
                      <BsCollection className="me-2" /> {!collapsed && 'Hosting'}
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </li>
        )}
        {canAccess('agenda') && (
          <li className={`nav-item ${isActive('/agenda') ? 'active' : ''}`}>
            <Link className="nav-link d-flex align-items-center" to="/agenda" onClick={handleNavigate}>
              <BsCalendar className="me-2" /> {!collapsed && 'Agenda'}
            </Link>
          </li>
        )}
        {canAccess('tareas') && (
          <li className={`nav-item ${isActive('/tareas') ? 'active' : ''}`}>
            <Link className="nav-link d-flex align-items-center" to="/tareas" onClick={handleNavigate}>
              <BsListCheck className="me-2" /> {!collapsed && 'Tareas'}
            </Link>
          </li>
        )}
        {canAccess('proyectos') && (
          <li className={`nav-item ${isActive('/proyectos') ? 'active' : ''}`}>
            <Link className="nav-link d-flex align-items-center" to="/proyectos" onClick={handleNavigate}>
              <BsKanban className="me-2" /> {!collapsed && 'Proyectos'}
            </Link>
          </li>
        )}
        {canAccess('proyectos') && (
          <li className={`nav-item ${isActive('/cambios') ? 'active' : ''}`}>
            <Link className="nav-link d-flex align-items-center" to="/cambios" onClick={handleNavigate} title="Cambios solicitados por clientes en sus webs y aplicaciones">
              <BsTools className="me-2" /> {!collapsed && 'Cambios'}
              {devAttention > 0 && (!collapsed
                ? <span className="badge bg-danger ms-auto">{devAttention}</span>
                : <span title={`${devAttention} cambios te esperan`} style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: '50%', background: '#dc3545', display: 'inline-block' }} />
              )}
            </Link>
          </li>
        )}
        {canAccess('jornada') && (
          <li className={`nav-item ${isActive('/jornada') ? 'active' : ''}`}>
            <Link className="nav-link d-flex align-items-center" to="/jornada" onClick={handleNavigate}>
              <BsClock className="me-2" /> {!collapsed && 'Jornada'}
            </Link>
          </li>
        )}
        {canAccess('galeria') && (
          <li className={`nav-item ${isActive('/galeria') ? 'active' : ''}`}>
            <Link className="nav-link d-flex align-items-center" to="/galeria" onClick={handleNavigate}>
              <BsImages className="me-2" /> {!collapsed && 'Galería'}
            </Link>
          </li>
        )}
        {canAccess('software') && (
          <li className={`nav-item ${isActive('/software') ? 'active' : ''}`}>
            <Link className="nav-link d-flex align-items-center" to="/software" onClick={handleNavigate}>
              <BsCpu className="me-2" /> {!collapsed && 'Software'}
            </Link>
          </li>
        )}
        {canAccess('monitorizacion') && (
          <li className={`nav-item ${isActive('/monitorizacion') ? 'active' : ''}`}>
            <Link className="nav-link d-flex align-items-center" to="/monitorizacion" onClick={handleNavigate}>
              <BsActivity className="me-2" /> {!collapsed && 'Monitorización'}
            </Link>
          </li>
        )}
        {canAccess('agentes') && (
          <li className={`nav-item ${isActive('/agentes') ? 'active' : ''}`}>
            <Link className="nav-link d-flex align-items-center" to="/agentes" onClick={handleNavigate}>
              <BsRobot className="me-2" /> {!collapsed && 'Agentes'}
            </Link>
          </li>
        )}
        {canAccessSupport && (
          <li className={`nav-item ${isActive('/soporte') ? 'active' : ''}`}>
            <Link className="nav-link d-flex align-items-center" to="/soporte" onClick={handleNavigate}>
              <BsHeadset className="me-2" /> {!collapsed && 'Soporte'}
              {supportUnread > 0 && (!collapsed
                ? <span className="badge bg-danger ms-auto">{supportUnread}</span>
                : <span title={`${supportUnread} sin leer`} style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: '50%', background: '#dc3545', display: 'inline-block' }} />
              )}
            </Link>
          </li>
        )}
      </ul>

        <div className="px-3 pb-3 mt-auto d-flex flex-column gap-2">
          {canAccess('usuarios') && (
            <Link className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center" to="/usuarios" onClick={handleNavigate}>
              <BsPerson className="me-2" /> {!collapsed && 'Usuarios'}
            </Link>
          )}
          {canAccess('configuracion') && (
            <Link className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center" to="/configuracion" onClick={handleNavigate}>
              <BsGear className="me-2" /> {!collapsed && 'Configuración'}
            </Link>
          )}
          {canUsePush && pushSupported && pushPermission !== 'unsupported' && (
            <button
              type="button"
              className={`btn w-100 d-flex align-items-center justify-content-center ${pushReady ? 'btn-outline-success' : 'btn-outline-secondary'}`}
              onClick={enablePush}
              disabled={pushSubscribing || pushReady}
              title={pushReady ? 'Notificaciones activas' : 'Activar notificaciones push'}
            >
              {pushReady ? <BsBellFill className="me-2" /> : <BsBell className="me-2" />}
              {!collapsed && (pushSubscribing ? 'Activando…' : (pushReady ? 'Notificaciones activas' : 'Activar notificaciones'))}
            </button>
          )}
          <button className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center" onClick={handleLogout}>
            <BsBoxArrowRight className="me-2" /> {!collapsed && 'Cerrar sesión'}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
